import {
  and,
  count,
  desc,
  eq,
  isNotNull,
  list,
  list_group,
  list_group_item,
  ne,
  type PowerSyncDrizzleDB,
  themes,
  todo,
} from "@listto/powersync";
import { liveListFilters, liveTodoFilters } from "../live/filters";

/**
 * Shared Drizzle query builder for Lists with their themes and group associations.
 */
export function getListsWithMetaQuery(
  db: PowerSyncDrizzleDB,
  userId: string,
  options: { limit?: number } = {},
) {
  const query = db
    .select({
      id: list.id,
      name: list.name,
      color: list.color,
      icon: list.icon,
      user_id: list.user_id,
      group_id: list_group_item.group_id,
      rel_id: list_group_item.id,
      theme_id: list.theme_id,
      is_blur: list.is_blur,
      is_shared: list.is_shared,
      created_at: list.created_at,
      updated_at: list.updated_at,
      t_is_dark: themes.is_dark,
      t_background: themes.background,
      t_surface: themes.surface,
      t_foreground: themes.foreground,
      t_field_background: themes.field_background,
      t_danger: themes.danger,
      t_success: themes.success,
      t_warning: themes.warning,
      t_muted: themes.muted,
      t_accent: themes.accent,
      t_glow_colors: themes.glow_colors,
      t_pattern_type: themes.pattern_type,
      t_pattern_opacity: themes.pattern_opacity,
      t_pair_id: themes.pair_id,
    })
    .from(list)
    .leftJoin(themes, eq(list.theme_id, themes.id))
    .leftJoin(list_group_item, eq(list.id, list_group_item.list_id))
    .where(and(eq(list.user_id, userId), ...liveListFilters()))
    .orderBy(desc(list.updated_at));

  if (options.limit !== undefined) {
    return query.limit(options.limit);
  }

  return query;
}

/**
 * Lightweight count query for lists — does not fetch row data.
 * Use this when only the total number of lists is needed (e.g., profile stats).
 */
export function getListsCountQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({ count: count(list.id).mapWith(Number) })
    .from(list)
    .where(and(eq(list.user_id, userId), ...liveListFilters()));
}

/**
 * Shared Drizzle query builder for List Groups.
 */
export function getListGroupsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(list_group)
    .where(eq(list_group.user_id, userId))
    .orderBy(desc(list_group.updated_at));
}

/**
 * Shared Drizzle query builder for Todo counts per list.
 */
export function getListTodoCountsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      list_id: todo.list_id,
      count: count(todo.id).mapWith(Number),
    })
    .from(todo)
    .where(
      and(
        ne(todo.completed, 1),
        isNotNull(todo.list_id),
        eq(todo.user_id, userId),
        ...liveTodoFilters(),
      ),
    )
    .groupBy(todo.list_id);
}

/**
 * Shared Drizzle query builder for a single list detail.
 */
export function getListDetailQuery(db: PowerSyncDrizzleDB, id: string) {
  return db
    .select({
      id: list.id,
      name: list.name,
      color: list.color,
      icon: list.icon,
      user_id: list.user_id,
      group_id: list_group_item.group_id,
      rel_id: list_group_item.id,
      theme_id: list.theme_id,
      is_blur: list.is_blur,
      is_shared: list.is_shared,
      created_at: list.created_at,
      updated_at: list.updated_at,
      t_is_dark: themes.is_dark,
      t_background: themes.background,
      t_surface: themes.surface,
      t_foreground: themes.foreground,
      t_field_background: themes.field_background,
      t_danger: themes.danger,
      t_success: themes.success,
      t_warning: themes.warning,
      t_muted: themes.muted,
      t_accent: themes.accent,
      t_glow_colors: themes.glow_colors,
      t_pattern_type: themes.pattern_type,
      t_pattern_opacity: themes.pattern_opacity,
      t_pair_id: themes.pair_id,
    })
    .from(list)
    .leftJoin(themes, eq(list.theme_id, themes.id))
    .leftJoin(list_group_item, eq(list.id, list_group_item.list_id))
    .where(eq(list.id, id))
    .limit(1);
}

/**
 * Maps the result of getListTodoCountsQuery to a Map for easier lookup.
 */
export function mapListTodoCounts(
  rows: readonly { list_id: string | null; count: number }[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (row.list_id) {
      map.set(row.list_id, row.count);
    }
  }
  return map;
}

/** Trash: soft-deleted lists of user, most recent first. */
export function getTrashedListsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: list.id,
      name: list.name,
      color: list.color,
      icon: list.icon,
      deleted_at: list.deleted_at,
    })
    .from(list)
    .where(and(eq(list.user_id, userId), isNotNull(list.deleted_at)))
    .orderBy(desc(list.deleted_at));
}
